import { Box, Skeleton, styled, Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useAccount, useChat } from "../../../context";
import { useMessageHandler, useResponsiveHeight } from "../../../hooks";
import { ChatDialogProps, MessageType } from "../../../interfaces/all";
import ChatHeader from "../ChatHeader/ChatHeader";
import ChatInput from "../ChatInput/ChatInput";
import ChatMessages from "../ChatMessages/ChatMessages";

const DialogContainer = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  position: relative;
`;

const RightContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100vh; /* Altura fixa para desktop */
  max-height: 100vh; /* Limitar altura máxima */
  background: #f0f2f5;
  position: relative;
  overflow: hidden; /* Prevenir scroll no container principal */

  /* Correções específicas para mobile */
  @media screen and (max-width: 768px) {
    height: 100vh;
    height: -webkit-fill-available;
    height: 100dvh;
    max-height: 100vh;
    max-height: -webkit-fill-available;
    max-height: 100dvh;
    min-height: 100vh;
    min-height: -webkit-fill-available;
    min-height: 100dvh;
  }

  /* Suporte para navegadores modernos */
  @supports (height: 100dvh) {
    @media screen and (max-width: 768px) {
      height: 100dvh;
      max-height: 100dvh;
      min-height: 100dvh;
    }
  }
`;

const ChatContent = styled(Box)`
  display: flex;
  flex: 1;
  position: relative;
  height: calc(100vh - 64px); /* Altura ajustada para header maior */
  max-height: calc(100vh - 64px); /* Limitar altura máxima */
  overflow: hidden;

  /* Correções específicas para mobile */
  @media screen and (max-width: 768px) {
    height: calc(100vh - 64px);
    height: calc(-webkit-fill-available - 64px);
    height: calc(100dvh - 64px);
    max-height: calc(100vh - 64px);
    max-height: calc(-webkit-fill-available - 64px);
    max-height: calc(100dvh - 64px);
    min-height: calc(100vh - 64px);
    min-height: calc(-webkit-fill-available - 64px);
    min-height: calc(100dvh - 64px);
  }

  /* Suporte para navegadores modernos */
  @supports (height: 100dvh) {
    @media screen and (max-width: 768px) {
      height: calc(100dvh - 64px);
      max-height: calc(100dvh - 64px);
      min-height: calc(100dvh - 64px);
    }
  }
`;

const ChatMain = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSearchOpen" &&
    prop !== "infoPanelWidth" &&
    prop !== "isResizing",
})<{ isSearchOpen: boolean; infoPanelWidth?: number; isResizing?: boolean }>(
  ({ isSearchOpen, infoPanelWidth = 350, isResizing = false }) => ({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%",
    minHeight: "100%",
    transition: isResizing ? "none" : "margin-right 0.15s ease-out",
    marginRight: isSearchOpen ? `${infoPanelWidth}px` : 0,
    position: "relative",
    zIndex: 1,

    /* Correções específicas para mobile */
    "@media screen and (max-width: 768px)": {
      height: "100%",
      minHeight: "100%",
      marginRight: 0, // Sempre sem margem em mobile
    },
  }),
);

const ChatDialog: React.FC<ChatDialogProps> = ({
  onSearchToggle,
  isSearchOpen,
  infoPanelWidth = 350,
  isInfoPanelResizing = false,
}) => {
  const { account, socket } = useAccount();
  const { selectedConversation } = useChat();
  const { setMessages } = useMessageHandler({
    socket,
    selectedConversation,
  });
  const [loading] = useState(false);
  const { getHeightStyles } = useResponsiveHeight();

  // Callback para quando todas as mensagens são marcadas como lidas
  const handleAllMessagesRead = useCallback(() => {
    // Emitir evento customizado para notificar outros componentes
    const event = new CustomEvent("allMessagesRead", {
      detail: {
        conversationId: selectedConversation?.id,
        phoneNumber:
          selectedConversation?.valid_telephone ||
          selectedConversation?.telephone,
        timestamp: new Date().toISOString(),
      },
    });
    window.dispatchEvent(event);
  }, [selectedConversation]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f0f2f5",
          ...getHeightStyles(),
        }}
      >
        <Box
          sx={{
            height: "60px",
            display: "flex",
            alignItems: "center",
            padding: "0 16px",
            borderBottom: "1px solid #e9edef",
            backgroundColor: "#ffffff",
          }}
        >
          <Skeleton variant="text" width="60%" height={24} />
        </Box>
        <Box sx={{ flex: 1, p: 2 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Box key={`chat-dialog-skeleton-${index}`} sx={{ mb: 2 }}>
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={16} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (!account) {
    return (
      <DialogContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Faça login para começar a conversar
          </Typography>
        </Box>
      </DialogContainer>
    );
  }

  const handleSearchToggle = (
    isOpen: boolean,
    type: "search" | "contact" = "search",
  ) => {
    onSearchToggle(isOpen, type);
  };

  const handleSendMessage = (message: MessageType) => {
    setMessages((prev) => [...prev, message]);
  };

  return (
    <DialogContainer>
      <RightContainer>
        <ChatHeader
          onSearchToggle={(isOpen, type) => handleSearchToggle(isOpen, type)}
          isSearchOpen={isSearchOpen}
          infoPanelWidth={infoPanelWidth}
          isInfoPanelResizing={isInfoPanelResizing}
        />
        <ChatContent>
          <ChatMain
            isSearchOpen={isSearchOpen}
            infoPanelWidth={infoPanelWidth}
            isResizing={isInfoPanelResizing}
          >
            <ChatMessages onAllRead={handleAllMessagesRead} />
            <ChatInput onSendMessage={handleSendMessage} />
          </ChatMain>
        </ChatContent>
      </RightContainer>
    </DialogContainer>
  );
};

export default ChatDialog;
